import { memo, useCallback, useMemo, useState } from 'react';
import { IconSended } from '@app/icons';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import { Modal, ModalProps, SelectOption } from '@app/components';
import Loader from 'components/Loader';
import { useForm } from '@app/forms';
import { Kato } from '@app/models';
import { useUserContext } from 'providers/UserProvider';
import { postInvite } from '@app/api';
import { KatoSelectTrigger } from '@app/components';

interface Props extends Partial<ModalProps> {}

interface IEmployee {
  iin: string;
  fullName: string;
  specialization: string;
  email: string;
}

function ModalAddEmployee(props: Props) {
  const { visible, onClose } = props;
  const [messageSended, onSendMessage] = useState(false);
  const user = useUserContext();
  const [isPending, setIsPending] = useState(false);
  const [kato, setKato] = useState<Kato | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const companyId = useMemo(() => user.company?.id, [user]);

  const onChangeKato = useCallback((option: SelectOption<Kato>) => {
    setKato(option.value);
  }, []);

  const onClearKato = useCallback(() => {
    setKato(undefined);
  }, []);

  // TODO: add validate and errors for form
  const { onChange, values } = useForm<IEmployee>({
    iin: '',
    fullName: '',
    specialization: '',
    email: '',
  });

  const onSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // TODO: add throw error
    if (companyId === undefined) return;

    setErrorMessage(null); // Clear previous errors
    setIsPending(true);

    try {
      await postInvite({ companyId, ...values, katoCode: kato?.code });
      setTimeout(() => {
        setIsPending(false);
        onSendMessage(true);
      }, 1500);
    } catch (error: any) {
      setIsPending(false);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Произошла ошибка при отправке инвайта';
      setErrorMessage(message);
    }
  };

  const onCloseModal = useCallback(() => {
    onSendMessage(false);
    setErrorMessage(null);
    if (!!onClose) {
      onClose();
    }
  }, [onClose]);

  if (!visible) {
    return null;
  }

  return (
    <Modal visible={visible} onClose={onCloseModal} size="large">
      <div className="flex flex-col items-center justify-center w-full">
        <div
          className={
            messageSended
              ? 'w-full flex flex-col items-center justify-center py-8'
              : 'w-full'
          }
        >
          {messageSended ? (
            <>
              {isPending ? (
                <span className="flex items-center justify-center h-24">
                  <Loader />
                </span>
              ) : (
                <>
                  <span className="flex items-center justify-center mb-4">
                    <IconSended />
                  </span>
                  <div className="text-lg font-semibold mb-2 text-center">
                    Инвайт отправлен!
                  </div>
                  <div className="flex justify-center">
                    <Button onClick={onCloseModal}>Понятно</Button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold">Добавить сотрудника</span>
              </div>
              <form
                onSubmit={onSubmitForm}
                className="flex flex-col gap-6 w-full"
              >
                {errorMessage && (
                  <div className="bg-destructive/15 border border-destructive text-destructive px-4 py-3 rounded-lg">
                    <p className="text-sm">{errorMessage}</p>
                  </div>
                )}
                <div className="bg-card rounded-lg p-4 sm:p-6 flex flex-col gap-6 shadow">
                  <div>
                    <div className="text-base font-semibold mb-3">Данные</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label
                          htmlFor="iin"
                          className="block text-sm font-medium mb-1"
                        >
                          ИИН
                        </label>
                        <Input
                          id="iin"
                          placeholder="Введите ИИН"
                          name="iin"
                          value={values.iin}
                          onChange={(e) =>
                            onChange(e.target.value, e.target.name)
                          }
                          maxLength={12}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="fullName"
                          className="block text-sm font-medium mb-1"
                        >
                          Ф.И.О.
                        </label>
                        <Input
                          id="fullName"
                          placeholder="Введите Ф.И.О."
                          name="fullName"
                          value={values.fullName}
                          onChange={(e) =>
                            onChange(e.target.value, e.target.name)
                          }
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="specialization"
                          className="block text-sm font-medium mb-1"
                        >
                          Сфера деятельности (должность)
                        </label>
                        <Input
                          id="specialization"
                          placeholder="Введите должность"
                          name="specialization"
                          value={values.specialization}
                          onChange={(e) =>
                            onChange(e.target.value, e.target.name)
                          }
                        />
                      </div>
                      <div>
                        <KatoSelectTrigger
                          label="регион"
                          value={(!!kato ? kato.nameRu : '') || undefined}
                          placeholder="Выберите регион"
                          onChange={onChangeKato}
                          onClickClear={onClearKato}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium mb-1"
                      >
                        Email
                      </label>
                      <Input
                        id="email"
                        placeholder="Введите электронный адрес (почту)"
                        name="email"
                        value={values.email}
                        onChange={(e) =>
                          onChange(e.target.value, e.target.name)
                        }
                        required
                        type="email"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-base font-semibold mb-3">Права</div>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center gap-2">
                        <Checkbox id="role1" />
                        <label
                          htmlFor="role1"
                          className="text-sm cursor-pointer"
                        >
                          Создание/редактирование объявлений
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="role2" />
                        <label
                          htmlFor="role2"
                          className="text-sm cursor-pointer"
                        >
                          Принять/отклонить отклики
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="role3" />
                        <label
                          htmlFor="role3"
                          className="text-sm cursor-pointer"
                        >
                          Просмотр объявлений и откликов
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="role4" />
                        <label
                          htmlFor="role4"
                          className="text-sm cursor-pointer"
                        >
                          Просмотр документов
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="role5" />
                        <label
                          htmlFor="role5"
                          className="text-sm cursor-pointer"
                        >
                          Откликаться на объявления от имени компании
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="role6" />
                        <label
                          htmlFor="role6"
                          className="text-sm cursor-pointer"
                        >
                          Добавлять новых сотрудников
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={isPending}>
                      {isPending ? 'Отправка...' : 'Добавить'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default memo(ModalAddEmployee);
